// What /pcm keeps in its URL: the ecosystem (`?ecossistema=`) and the open tab (`?aba=`), so a
// reload, the back button or a shared link opens the same view. A value equal to its default is
// left out of the URL; a value outside the vocabulary is the link being wrong, and the route
// renders InvalidLinkPage for it.
import { Option, Schema } from 'effect';
import { Ecosystem } from '@finnest/power-admin-contracts';
import type { EcosystemControl } from '../../regulatory/ecosystem/requested-ecosystem.tsx';
import { EcosystemSearch } from '../../routes/search/ecosystem-search.ts';

export const PCM_TAB_IDS = [
  'hoje',
  'aprovacao',
  'lotes',
  'pendencias',
  'fechamentos',
  'rastrear',
  'configuracao',
] as const;

export const PcmTabSchema = Schema.Literals(PCM_TAB_IDS);
export type PcmTab = typeof PcmTabSchema.Type;

export const PCM_TAB_LABELS = {
  hoje: 'Hoje',
  aprovacao: 'Aguardando aprovação',
  lotes: 'Lotes',
  pendencias: 'Pendências',
  fechamentos: 'Fechamentos',
  rastrear: 'Rastrear chamada',
  configuracao: 'Configuração',
} as const satisfies Record<PcmTab, string>;

const DEFAULT_TAB = 'hoje' satisfies PcmTab;

// Tabs only some ecosystems serve: another ecosystem drops them, as it drops its own filters.
const ECOSYSTEM_TABS: ReadonlySet<PcmTab> = new Set(['pendencias']);

export const PcmSearch = Schema.Struct({
  ...EcosystemSearch.fields,
  aba: Schema.optionalKey(PcmTabSchema),
});
export type PcmSearch = typeof PcmSearch.Type;

// The same URL, each key read as present or not: encoding `None` leaves the key out.
const PcmUrl = Schema.Struct({
  ecossistema: Schema.OptionFromOptionalKey(Ecosystem),
  aba: Schema.OptionFromOptionalKey(PcmTabSchema),
});
const encodeUrl = Schema.encodeSync(PcmUrl);

/** The view a PCM link opens. */
export type PcmLink = {
  readonly ecosystem: Option.Option<Ecosystem>;
  readonly tab: PcmTab;
};

/** The tab the link opens, and how the page asks for another one. */
export type PcmTabControl = {
  readonly tab: PcmTab;
  readonly onTab: (tab: PcmTab) => void;
};

/** What the PCM page shows from its link, and how it asks for another view. */
export type PcmControl = EcosystemControl & PcmTabControl;

/** The view `search` names; what it leaves out is the default. */
export const pcmLinkOf = (search: PcmSearch): PcmLink => ({
  ecosystem: Option.fromUndefinedOr(search.ecossistema),
  tab: search.aba ?? DEFAULT_TAB,
});

/** The search of a link to `link`, naming only what differs from the default view. */
export const pcmSearchOf = ({ ecosystem, tab }: PcmLink): PcmSearch =>
  encodeUrl({
    ecossistema: ecosystem,
    aba: Option.liftPredicate(tab, (asked) => asked !== DEFAULT_TAB),
  });

/** Another ecosystem keeps the tab, unless the tab belongs to the ecosystem left. */
export const withEcosystem = (link: PcmLink, ecosystem: Option.Option<Ecosystem>): PcmLink => ({
  ecosystem,
  tab: ECOSYSTEM_TABS.has(link.tab) ? DEFAULT_TAB : link.tab,
});
